import { useMemo } from 'react';
import { FileText, Check, User, Home, Heart, CreditCard, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card';
import { Badge } from '~/components/ui/badge';
import { Separator } from '~/components/ui/separator';
import { StepHeader } from '~/components/forms/StepHeader';
import { AcknowledgmentsChunk } from '~/components/form-chunks/AcknowledgmentsChunk';

interface AppReviewStepProps {
  formData: any;
  updateFormData: (path: string, value: any) => void;
}

export function AppReviewStep({ formData, updateFormData }: AppReviewStepProps) {
  const acknowledgments = formData?.acknowledgments || {};

  const summary = useMemo(() => {
    const persisted = (formData?.students || []).filter((s: any) => s.firstName || s.lastName);
    const newOnes = (formData?.newStudents || []).filter((s: any) => s.firstName || s.lastName);
    const students = [...persisted, ...newOnes];
    const familyInfo = formData?.familyInfo || {};
    const address = familyInfo.address || {};
    const tuition = formData?.tuitionPreferences || {};
    const availableOptions = formData?.availableOptions || {};
    const tuitionOptions = availableOptions.tuitionOptions || [];
    const paymentOptions = availableOptions.paymentOptions || [];

    return {
      students,
      familyInfo,
      address,
      tuition,
      tuitionOptions,
      paymentOptions,
    };
  }, [formData]);

  const handleAcknowledgmentsUpdate = (updatedAcknowledgments: any) => {
    updateFormData('acknowledgments', updatedAcknowledgments);
  };

  const canSubmit = acknowledgments.infoAccurate;

  return (
    <div className="space-y-6">
      <StepHeader
        icon={FileText}
        title="Review & Submit"
        description="Please review your information before submitting."
      />

      <div className="max-w-2xl mx-auto space-y-6">
        {/* Students Summary */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <User className="h-5 w-5" />
              Students ({summary.students.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {summary.students.map((student: any) => (
              <div key={student.studentEnrollmentId || student.tempId} className="flex items-center justify-between">
                <span>{student.firstName} {student.lastName}</span>
                <Badge variant="secondary">
                  {student.gradeLevel || 'Grade TBD'}
                </Badge>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Address Summary */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Home className="h-5 w-5" />
              Address
            </CardTitle>
          </CardHeader>
          <CardContent>
            {summary.address.street ? (
              <p className="text-sm">
                {summary.address.street}
                {summary.address.street2 && <><br />{summary.address.street2}</>}
                {(summary.address.city || summary.address.state || summary.address.zip) && (
                  <><br />{summary.address.city}, {summary.address.state} {summary.address.zip}</>
                )}
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">No address provided</p>
            )}
          </CardContent>
        </Card>

        {/* Medical Summary */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Heart className="h-5 w-5" />
              Medical Information
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2 text-sm">
              <Check className="h-4 w-4 text-green-500" />
              <span>Medical information provided for {summary.students.length} student{summary.students.length !== 1 ? 's' : ''}</span>
            </div>
          </CardContent>
        </Card>

        {/* Tuition Summary */}
        {(() => {
          const studentIds = summary.students.map((s: any) => s.studentEnrollmentId || s.tempId);
          const studentSelections = summary.tuition?.studentSelections || {};
          const familyPaymentOptionKey = summary.tuition?.paymentOptionKey;

          const allStudentsHaveTuition = studentIds.length > 0 && studentIds.every((id: string) => {
            const sel = studentSelections[id];
            return sel?.tuitionOptionKey;
          });
          const hasPaymentOption = !!familyPaymentOptionKey;
          const allComplete = allStudentsHaveTuition && hasPaymentOption;

          return (
            <Card className={!allComplete ? 'border-amber-300' : ''}>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  Tuition Preferences
                  {!allComplete && (
                    <AlertCircle className="h-4 w-4 text-amber-500" />
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {!allComplete ? (
                  <p className="text-sm text-amber-600 flex items-center gap-2">
                    <AlertCircle className="h-4 w-4" />
                    {!allStudentsHaveTuition
                      ? 'Please go back and select a tuition option for all students'
                      : 'Please go back and select a payment plan'}
                  </p>
                ) : (
                  <>
                    {studentIds.map((studentId: string) => {
                      const selection = studentSelections[studentId];
                      if (!selection?.tuitionOptionKey) return null;

                      const student = summary.students.find((s: any) => (s.studentEnrollmentId || s.tempId) === studentId);
                      const studentName = student ? `${student.firstName} ${student.lastName}` : 'Student';

                      const tuitionOption = summary.tuitionOptions.find((t: any) => t.key === selection.tuitionOptionKey);
                      const tuitionName = tuitionOption?.name || selection.tuitionOptionKey;

                      return (
                        <div key={studentId} className="text-sm border-b pb-2 last:border-0 last:pb-0">
                          <p className="font-medium mb-1">{studentName}</p>
                          <p>
                            <span className="text-muted-foreground">Tuition:</span>{' '}
                            {tuitionName}
                          </p>
                        </div>
                      );
                    })}
                    <Separator className="my-2" />
                    {(() => {
                      const paymentOption = summary.paymentOptions.find((p: any) => p.key === familyPaymentOptionKey);
                      const paymentName = paymentOption?.name || familyPaymentOptionKey;
                      return (
                        <p className="text-sm">
                          <span className="font-medium">Payment Plan:</span>{' '}
                          {paymentName}
                        </p>
                      );
                    })()}
                    <p className="text-sm">
                      <span className="font-medium">Financial Assistance:</span>{' '}
                      {summary.tuition?.seekingFinancialAssistance ? 'Requested' : 'Not requested'}
                    </p>
                  </>
                )}
              </CardContent>
            </Card>
          );
        })()}

        {/* Acknowledgments */}
        <AcknowledgmentsChunk
          acknowledgments={acknowledgments}
          onUpdate={handleAcknowledgmentsUpdate}
          isApplication={true}
        />

        {!canSubmit && (
          <div className="flex items-center gap-2 text-amber-600 text-sm justify-center">
            <AlertCircle className="h-4 w-4" />
            <span>Please check all acknowledgments before submitting</span>
          </div>
        )}
      </div>
    </div>
  );
}
